import { Delegate } from '@theme/delegate';
import { DrawerComponent } from '@theme/drawer-component';
import { responsive } from '@theme/responsive';
import { RevolutionAnimation } from '@theme/revolution-animation';

class PdpDropdownDrawer extends DrawerComponent {
  connectedCallback() {
    if (this.parentElement !== document.body) {
      document.body.appendChild(this);
    }
    super.connectedCallback();
  }

  onClosed() {
    this.querySelector('.pdp-dropdown-group__drawer-inner')?.replaceChildren();
  }
}

class PdpDropdownGroup extends HTMLElement {
  #delegate = null;
  #drawerDelegate = null;
  #lock = false;
  #drawer = null;
  #onBreakpoint = null;

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    const drawerId = this.dataset.drawerId;
    this.#drawer = drawerId ? document.getElementById(drawerId) : null;

    this.#delegate = new Delegate(this);
    this.#delegate.on('click', '[data-action="toggle-pdp-dropdown"]', this.#toggleDropdown);

    if (this.#drawer) {
      this.#drawerDelegate = new Delegate(this.#drawer);
      this.#drawerDelegate.on('click', '[data-action="toggle-pdp-dropdown"]', this.#toggleDropdown);
    }

    responsive.addBreakpointListener('lap_and_up');
    this.#onBreakpoint = (event) => {
      if (!event.detail.matches && this.#drawer?.isOpen) {
        this.#drawer.close();
      }
    };
    document.body.addEventListener('breakpoint:lap_and_up', this.#onBreakpoint);
  }

  disconnectedCallback() {
    this.#delegate?.off();
    this.#delegate = null;
    this.#drawerDelegate?.off();
    this.#drawerDelegate = null;
    document.body.removeEventListener('breakpoint:lap_and_up', this.#onBreakpoint);
    this.#onBreakpoint = null;
  }

  #toggleDropdown = (event, target) => {
    event.preventDefault();
    if (this.#lock) return;

    if (target.dataset.openSizeGuide === 'true') {
      this.#openSizeGuideDrawer(target);
      return;
    }

    const isInsideDrawer = Boolean(target.closest('pdp-dropdown-drawer'));

    if (responsive.matchesBreakpoint('lap_and_up') && !isInsideDrawer) {
      this.#openDesktopDrawer(target);
      return;
    }

    const dropdown = target.closest('.pdp-dropdown');
    if (!dropdown) return;

    const isOpen = target.getAttribute('aria-expanded') === 'true';

    if (isOpen) {
      this.#closeDropdown(dropdown);
      return;
    }

    this.#openDropdown(dropdown, target);
  };

  #openDesktopDrawer(target) {
    const dropdown = target.closest('.pdp-dropdown');
    if (!dropdown?.dataset.dropdownId) return;

    const dropdownId = dropdown.dataset.dropdownId;
    const drawerInner = this.#drawer?.querySelector('.pdp-dropdown-group__drawer-inner');
    if (!drawerInner) return;

    this.#populateDrawer(drawerInner);

    if (!this.#drawer.isOpen) {
      this.#drawer.open();
    }

    this.#activateDrawerDropdown(drawerInner, dropdownId);
  }

  #populateDrawer(drawerInner) {
    if (drawerInner.children.length) return;

    this.querySelectorAll(':scope > .pdp-dropdown-group__inner > .pdp-dropdown').forEach((item) => {
      drawerInner.appendChild(item.cloneNode(true));
    });
  }

  #activateDrawerDropdown(drawerInner, dropdownId) {
    drawerInner.querySelectorAll('.pdp-dropdown').forEach((item) => {
      if (item.dataset.dropdownId === dropdownId) return;

      const button = item.querySelector('.Dropdown--Button');
      if (button?.getAttribute('aria-expanded') === 'true') {
        this.#closeDropdown(item, true);
      }
    });

    const targetInDrawer = drawerInner.querySelector(`[data-dropdown-id="${CSS.escape(dropdownId)}"]`);
    if (!targetInDrawer) return;

    const button = targetInDrawer.querySelector('[data-action="toggle-pdp-dropdown"]');
    if (!button || button.getAttribute('aria-expanded') === 'true') return;

    // Instant, not slideDown: the drawer is already sliding in, and a second animation
    // behind it reads as the row opening late (869eaj2te). Expanding without a transition
    // also skips a scrollHeight read taken while the panel is still off-screen.
    this.#openDropdown(targetInDrawer, button, true);
  }

  #openSizeGuideDrawer(target) {
    this.querySelectorAll(':scope > .pdp-dropdown-group__inner > .pdp-dropdown').forEach((item) => {
      if (item.querySelector('[aria-expanded="true"]')) {
        this.#closeDropdown(item, true);
      }
    });

    const drawerId = target.dataset.sizeGuideId;
    if (!drawerId) return;

    const drawer = document.getElementById(drawerId);
    if (typeof drawer?.open !== 'function') return;

    // Handing off from the FAQ drawer: both panels are Drawer--right on the same edge with
    // the same 0.35s slide, so closing one while opening the other makes them cross on
    // screen. Figma 12959-102527 asks for an instant swap in this one case — drop the
    // transition on both for the swap frame, then hand the slide straight back so a normal
    // open/close of either drawer still animates.
    const handoff = this.#drawer?.isOpen ? [this.#drawer, drawer] : [];
    handoff.forEach((panel) => panel.classList.add('Drawer--instant'));

    this.#drawer?.close();
    drawer.open();

    if (!handoff.length) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => handoff.forEach((panel) => panel.classList.remove('Drawer--instant')));
    });
  }

  #openDropdown(dropdown, target, instant = false) {
    this.#lock = true;

    const button = dropdown.querySelector('.Dropdown--Button');
    const content = dropdown.querySelector('.Dropdown--Content');
    if (!button || !content) {
      this.#lock = false;
      return;
    }

    const scope = dropdown.closest('pdp-dropdown-drawer') ?? this;
    scope.querySelectorAll('.pdp-dropdown').forEach((item) => {
      if (item !== dropdown && item.querySelector('[aria-expanded="true"]')) {
        this.#closeDropdown(item, true);
      }
    });

    if (instant) {
      // Same end state slideDown() settles on, without the transition — so no
      // transitionend fires and the lock has to be dropped here.
      button.setAttribute('aria-expanded', 'true');
      content.setAttribute('aria-hidden', 'false');
      content.style.height = 'auto';
      content.style.pointerEvents = 'all';
      this.#lock = false;
      return;
    }

    const onTransitionEnd = (transitionEvent) => {
      if (transitionEvent.propertyName === 'height') {
        this.#lock = false;
        content.removeEventListener('transitionend', onTransitionEnd);
      }
    };

    content.addEventListener('transitionend', onTransitionEnd);
    button.setAttribute('aria-expanded', 'true');
    new RevolutionAnimation(content, 'true').slideDown();
  }

  #closeDropdown(dropdown, ignoreLock = false) {
    if (this.#lock && !ignoreLock) return;

    this.#lock = true;

    const button = dropdown.querySelector('.Dropdown--Button');
    const content = dropdown.querySelector('.Dropdown--Content');
    if (!button || !content) {
      this.#lock = false;
      return;
    }

    const onTransitionEnd = (transitionEvent) => {
      if (transitionEvent.propertyName === 'height') {
        if (content.getAttribute('aria-hidden') === 'false') {
          content.setAttribute('aria-hidden', 'true');
        }
        this.#lock = false;
        content.removeEventListener('transitionend', onTransitionEnd);
      }
    };

    content.addEventListener('transitionend', onTransitionEnd);
    new RevolutionAnimation(content, 'true').slideUp();
    button.setAttribute('aria-expanded', 'false');
  }
}

if (!customElements.get('pdp-dropdown-drawer')) {
  customElements.define('pdp-dropdown-drawer', PdpDropdownDrawer);
}

if (!customElements.get('pdp-dropdown-group')) {
  customElements.define('pdp-dropdown-group', PdpDropdownGroup);
}
